/// <amd-module name="Dashboards/RateLimiter/DashboardThrottlingService" />
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext, IVssService, Services, VssObservableService, VssService } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { HighResourceUsageInformation, IThrottleOnHighResourceUsage } from "./IThrottleHighResourceUsage";

/** Manages and exposes state for throttling of auto-refresh for Dashboard clients which have been using resources heavily.   */
export interface IDashboardThrottlingService extends VssObservableService<DashboardThrottlingInfo>, IThrottleOnHighResourceUsage {
    /**
     * Signal to Dashboard throttling service to start the auto-refresh throttle state, as that the user has tripped high resource usage.
     */
    dismissThrottlingMessage(): void;

    /**
     * Query the Dashboard Throttling service to see if the user is marked for throttling due to high resource usage.
     */
    getThrottlingState(): DashboardThrottlingInfo;
}

export interface DashboardThrottlingInfo {
    isThrottled: boolean;
    throttleMessageVisible: boolean;
}

/**
 * Acts as a transport agnostic central store of throttling state. Currently, this state is account scoped for the life of the browser session.
 *
 * -Reacts to incoming throttling notifications for monitor implementations for New and Old platform clients
 * -Exposes current state to New and Old Platform Dashboards
 * -Encapsulates secret of duration and scope of Dashboard throttling (e.g. user-account or user-dashboard)
 */
class DashboardThrottlingService extends VssObservableService<DashboardThrottlingInfo> {
    private static _clientThrottleDurationinMS = 4 * 60 * 60 * 1000;

    private _isThrottled: boolean = false;
    private _throttleMessageIsActive: boolean = false;

    private _timerManager: TimerManagement;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this._timerManager = new TimerManagement();
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        this._timerManager.dispose();
        super._serviceEnd(pageContext);
    }

    /**
     * Notify a throttling service to recommend that the user be throttled due to high resource usage.
     * The first occurrence is tracked, subsequent occurrences renew the throttle duration without additional tracking.
     */
    public flagAsThrottled(usageInformation: HighResourceUsageInformation): void {
        this._timerManager.clearAllTimers();
        this._timerManager.setTimeout(this.onTimerExpired.bind(this), DashboardThrottlingService._clientThrottleDurationinMS);

        // If the user was not already throttled, ensure the throttle message is visible.
        if (!this.isThrottled()) {
            this._throttleMessageIsActive = true;
            this._notify(
                {
                    isThrottled: true,
                    throttleMessageVisible: this.isThrottleMessageVisible()
                },
                "dashboardthrottled"
            );

            this.pageContext
                .getService<IVssTelemetryService>("IVssTelemetryService")
                .publishEvent("Dashboards", "ThrottlingService_UsageFlagged", usageInformation);
            this._isThrottled = true;
        }
    }

    /**
     * Signal to Dashboard throttling service to start the auto-refresh throttle state, as that the user has tripped high resource usage.
     */
    public dismissThrottlingMessage(): void {
        if (this._throttleMessageIsActive) {
            this._throttleMessageIsActive = false;
            this._notify(
                {
                    isThrottled: this.isThrottled(),
                    throttleMessageVisible: false
                },
                "throttleMessageDismissed"
            );
        }
    }

    public getThrottlingState(): DashboardThrottlingInfo {
        return {
            isThrottled: this.isThrottled(),
            throttleMessageVisible: this.isThrottleMessageVisible()
        };
    }

    private onTimerExpired(): void {
        this._throttleMessageIsActive = false;
        this._isThrottled = false;
        this._timerManager.clearAllTimers();

        this._notify(
            {
                isThrottled: this.isThrottled(),
                throttleMessageVisible: false
            },
            "throttlingIntervalExpired"
        );

        this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService").publishEvent("Dashboards", "ThrottlingService_FlagExpired", {});
    }

    /**
     * Encapsulates secret of determining if user is throttled. Liable to change, if duration needs to be persisted.
     */
    private isThrottled(): boolean {
        return this._isThrottled;
    }

    private isThrottleMessageVisible(): boolean {
        return this._throttleMessageIsActive;
    }
}

Services.add("IDashboardThrottlingService", { serviceFactory: DashboardThrottlingService });
